import dayjs from 'dayjs';

import { AdProduct, DateRange, MinMaxNumberValues, MinMaxStringValues } from '../../../apis/graphql/types';
import CampaignsTooltip from '../../../components/baymax/CampaignsTooltip';
import { BadgeProps } from '../../../components/BlueTheme';
import { defaultDateFormat } from '../../../constants/dates';
import { formatNumber } from '../../../utils/formatting';
import { DATE_RANGE_LABELS } from '../constants/reports';
import { capitalizeFirstLetter } from '../Report/Shared/utils';
import { CombinedFilters, DynamicFilters, DynamicFiltersFields, StaticFilters } from '../types/reports';

const filterLabel: Record<keyof CombinedFilters, string> = {
  campaigns: 'Sales Orders',
  dateRange: 'Date Range',
  revenue: 'Booked Spend',
  cpm: 'CPM',
  orderGoal: 'Order Goal',
  startDate: 'Line Start Date',
  endDate: 'Line End Date',
  adProduct: 'Ad Product',
  publisher: 'Publisher',
  deliveryGoal: 'Order Goal',
  lifetimeCompletions: 'Lifetime Completions',
};

export const getDateRange = (start?: string | null, end?: string | null): string | null => {
  if (!start || !end) {
    return null;
  }

  return `${dayjs(start).format(defaultDateFormat)}-${dayjs(end).format(defaultDateFormat)}`;
};

export const getBadgeTextForMinMaxFilters = (manMaxValues: MinMaxNumberValues, showCurrency: boolean): string => {
  const { min, max } = manMaxValues;
  if (!max)
    return `> ${formatNumber({
      figure: min || 0,
      dollarValue: showCurrency,
    })}`;
  if (!min)
    return `< ${formatNumber({
      figure: max || 0,
      dollarValue: showCurrency,
    })}`;
  return `${formatNumber({
    figure: min || 0,
    dollarValue: showCurrency,
  })} - ${formatNumber({
    figure: max || 0,
    dollarValue: showCurrency,
  })}`;
};

const getBadgeObject = (
  key: DynamicFiltersFields,
  text: string,
  removeFilter?: (id: DynamicFiltersFields) => void
): BadgeProps => {
  return {
    text,
    onRemove: removeFilter
      ? (): void => {
          removeFilter(key);
        }
      : undefined,
  };
};

const getAdProductBadges = (
  key: DynamicFiltersFields,
  value?: AdProduct[],
  removeFilter?: (id: DynamicFiltersFields) => void
): BadgeProps | null => {
  if (!value || !value.length) return null;

  return getBadgeObject(
    key,
    `${filterLabel[key]}: ${value.map((el) => capitalizeFirstLetter(el))?.join(',')}`,
    removeFilter
  );
};

const getDateRangeBadges = (
  key: DynamicFiltersFields,
  value?: MinMaxStringValues,
  removeFilter?: (id: DynamicFiltersFields) => void
): BadgeProps | null => {
  if (!value?.min && !value?.max) return null;
  const start = value?.min;
  const end = value?.max;
  const description = end
    ? `${dayjs(start).format(defaultDateFormat)}-${dayjs(end).format(defaultDateFormat)}`
    : `Starts from ${dayjs(start).format(defaultDateFormat)}`;

  return getBadgeObject(key, `${filterLabel[key]}: ${description}`, removeFilter);
};

const getCurrencyBadgeText = (
  key: DynamicFiltersFields,
  value?: MinMaxNumberValues,
  removeFilter?: (id: DynamicFiltersFields) => void
): BadgeProps | null => {
  if (!value?.min && !value?.max) return null;

  const showCurrency = key === 'cpm' || key === 'revenue';

  return getBadgeObject(key, `${filterLabel[key]}: ${getBadgeTextForMinMaxFilters(value, showCurrency)}`, removeFilter);
};

export const getDynamicFiltersBadges = (
  dynamicFilters?: DynamicFilters,
  removeFilter?: (id: DynamicFiltersFields) => void
): BadgeProps[] => {
  const dynamicBadges: BadgeProps[] = [];
  if (!dynamicFilters) return [];
  const entries = Object.keys(dynamicFilters) as (keyof DynamicFilters)[];
  entries.forEach((key) => {
    switch (key) {
      case 'cpm':
      case 'revenue':
      case 'orderGoal':
      case 'deliveryGoal':
      case 'lifetimeCompletions':
        const badge = getCurrencyBadgeText(key, dynamicFilters[key], removeFilter);
        if (badge) dynamicBadges.push(badge);
        break;
      case 'startDate':
      case 'endDate':
        const dateBadge = getDateRangeBadges(key, dynamicFilters[key], removeFilter);
        if (dateBadge) dynamicBadges.push(dateBadge);
        break;
      case 'adProduct':
        const adPropductBadge = getAdProductBadges(key, dynamicFilters[key], removeFilter);
        if (adPropductBadge) dynamicBadges.push(adPropductBadge);
        break;
    }
  });
  return dynamicBadges;
};

export const getBasedFiltersBadges = (basedFilters: StaticFilters): BadgeProps[] => {
  const badges: BadgeProps[] = [];
  const campaignNames: string[] =
    basedFilters.campaigns?.reduce((names: string[], campaign) => {
      if (campaign.name) {
        names.push(campaign.name);
      }
      return names;
    }, []) || [];
  badges.push({
    text: filterLabel.campaigns,
    counter: basedFilters.campaigns?.length || 0,
    ...(campaignNames.length && { tooltipContent: <CampaignsTooltip campaignNames={campaignNames} /> }),
  });

  const dateRange: DateRange | undefined = basedFilters.dateRange as DateRange;

  if (dateRange) {
    let badgeText =
      dateRange !== DateRange.Custom
        ? DATE_RANGE_LABELS[dateRange]
        : getDateRange(basedFilters.startDate, basedFilters.endDate);

    if (badgeText) {
      badges.push({
        text: `${filterLabel.dateRange}: ${badgeText || ''}`,
      });
    }
  }
  return badges;
};

export const getBadgeFilters = (
  basedFilters: StaticFilters,
  dynamicFilters?: DynamicFilters,
  removeFilter?: (id: DynamicFiltersFields) => void
): BadgeProps[] => {
  return [...getBasedFiltersBadges(basedFilters), ...getDynamicFiltersBadges(dynamicFilters, removeFilter)];
};
